import { ActivityStatus, ActivityType, type ActivityDTO, isProjectRoleEditable } from 'src/types/dto';
import type { GridColDef } from '@mui/x-data-grid';
import { AppDataGrid } from 'src/components/app-data-grid';

import type { InspectorDTO } from 'src/types/inspector';
import type { IProjectRole } from 'src/types/project-role';

import { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { gridClasses } from '@mui/x-data-grid';
import {
  Box,
  Stack,
  Button,
  Typography,
  Select,
  MenuItem,
  FormControl,
  Avatar,
  TextField,
} from '@mui/material';

import { formatInspectorCityStateCountry } from 'src/utils/inspector-location';
import { spacingUtil } from 'src/utils/spacing-util';
import { roundDuration } from 'src/utils/duration';
import {
  formatNumberToMaxPlaces,
  getMaxDecimalPlacesPattern,
  isNonNegativeDecimalWithMaxPlaces,
  normalizeDecimalInputToMaxPlaces,
  validatePositiveDecimalMaxPlacesInput,
} from 'src/utils/decimal-places';
import {
  getActivityDurationDays,
  isDurationRowEditable,
  MAX_NON_COUNTING_ALLOCATION_DAYS,
  resolveDurationInputBlur,
} from 'src/sections/visit/components/allocation-duration';
import { appointmentDisplayDurationDays } from 'src/utils/appointments';

import { Iconify } from 'src/components/iconify';
import { UserIcon } from 'src/components/user-icon';
import { AuthorizationsTooltip } from 'src/components/authorizations-tooltip';
import { InspectorDistanceCell } from './inspector-distance-cell';
import { getRemovableInspectorDateLabel } from '../allocation-date-label';
import {
  ALLOCATION_DATE_COLUMN_MIN_WIDTH,
  allocationDatePillSx,
  maxAllocationDatePillColumnWidth,
} from '../styled';
import { getConsumedUniqueProjectRoleIds } from 'src/sections/visit/components/unique-project-role';
import type { CompetencyHierarchy } from 'src/utils/competency-hierarchy';
import { BusinessUnitCell } from './business-unit-cell';

const PLACEHOLDER_ROW_ID = -1;

const MAX_DURATION_DECIMAL_PLACES = 5;

/**
 * Editable per-resource audit-day input for counting rows in the Team table.
 *
 * `maxDays` is this row's ceiling: the activity duration less the days the other counting
 * rows already hold. Values above it are flagged rather than committed, so the counting
 * total can never be pushed past the activity duration.
 */
function InspectorDurationInput({
  value,
  maxDays,
  allowClear = false,
  onCommit,
}: {
  value: number;
  maxDays: number;
  allowClear?: boolean;
  onCommit: (value: number) => void;
}) {
  const { t } = useTranslation('activities');
  const [text, setText] = useState(() =>
    formatNumberToMaxPlaces(value, MAX_DURATION_DECIMAL_PLACES)
  );
  const [error, setError] = useState(false);

  useEffect(() => {
    setText(formatNumberToMaxPlaces(value, MAX_DURATION_DECIMAL_PLACES));
    // Surface the over-budget state as soon as this row's budget shrinks below its stored
    // days (a sibling edit or a role flip), not only once the field is touched.
    setError(maxDays <= 0 || value > maxDays);
  }, [value, maxDays]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = normalizeDecimalInputToMaxPlaces(e.target.value, MAX_DURATION_DECIMAL_PLACES);
    setText(raw);

    if (raw === '' || !isNonNegativeDecimalWithMaxPlaces(raw, MAX_DURATION_DECIMAL_PLACES)) {
      setError(false);
      return;
    }
    const num = Number(raw);
    if (num <= 0) {
      setError(false);
      return;
    }
    if (num > maxDays) {
      setError(true);
      return;
    }
    setError(false);
    onCommit(roundDuration(num));
  };

  const handleBlur = () => {
    const next = resolveDurationInputBlur({ text, value, maxDays, allowClear });
    setText(next.text);
    setError(next.error);
    if (next.commit != null) {
      onCommit(next.commit);
    }
  };

  return (
    <TextField
      value={text}
      onChange={handleChange}
      onBlur={handleBlur}
      size="small"
      error={error}
      helperText={
        error
          ? t('allocation.details.filters.durationDays.exceedsMax', {
              max: formatNumberToMaxPlaces(maxDays, MAX_DURATION_DECIMAL_PLACES),
            })
          : undefined
      }
      inputProps={{
        inputMode: 'decimal',
        pattern: getMaxDecimalPlacesPattern(MAX_DURATION_DECIMAL_PLACES),
        'aria-label': t('allocation.details.filters.durationDays.label'),
      }}
      onKeyDown={(e) => validatePositiveDecimalMaxPlacesInput(e, MAX_DURATION_DECIMAL_PLACES)}
      sx={{ width: 90 }}
    />
  );
}

interface SelectedInspectorsListProps {
  selectedInspectors: (InspectorDTO & {
    selectedDurationDays?: number;
    countsTowardAudit?: boolean;
    currentProjectRoleId?: number;
  })[];
  activity: ActivityDTO;
  projectRoles?: IProjectRole[];
  projectRolesLoading?: boolean;
  inspectorProjectRoles?: Map<number, number | undefined>;
  /** Inspector IDs that can be removed from the pending selection (date cell click). */
  removableInspectorIds?: number[];
  onRemoveInspector?: (inspectorId: number) => void;
  /** Change project role for a pending selected inspector (triggers duration re-split). */
  onProjectRoleChange?: (inspectorId: number, projectRoleId: number) => void;
  /** Commit an edited per-resource audit-day value (counting rows only). */
  onInspectorDurationChange?: (inspectorId: number, durationDays: number) => void;
  onRevert?: () => void;
  onSave?: () => void;
  canSave?: boolean;
  canRevert?: boolean;
  /** Disable role edits and row removal while Approve is in progress. */
  isSaving?: boolean;
  accreditationBodyIds?: number[];
  activityCompetencyIds?: number[];
  competencyHierarchy?: CompetencyHierarchy;
  distanceLoading?: boolean;
}

const SelectedInspectorsList = ({
  selectedInspectors,
  activity,
  projectRoles = [],
  projectRolesLoading = false,
  inspectorProjectRoles,
  removableInspectorIds = [],
  onRemoveInspector,
  onProjectRoleChange,
  onInspectorDurationChange,
  onRevert,
  onSave,
  canSave = false,
  canRevert = false,
  isSaving = false,
  accreditationBodyIds,
  activityCompetencyIds,
  competencyHierarchy,
  distanceLoading = false,
}: SelectedInspectorsListProps) => {
  const { t } = useTranslation(['activities', 'common']);
  const removableIds = new Set(removableInspectorIds);
  const isEmpty = selectedInspectors.length === 0;

  const rows = useMemo(
    () => (isEmpty ? [{ id: PLACEHOLDER_ROW_ID, isPlaceholder: true } as any] : selectedInspectors),
    [isEmpty, selectedInspectors]
  );

  const scheduledColumnWidth = useMemo(() => {
    if (isEmpty) {
      return ALLOCATION_DATE_COLUMN_MIN_WIDTH;
    }

    const cellHorizontalPaddingPx = 16;

    return (
      maxAllocationDatePillColumnWidth(
        selectedInspectors.map((row) => getRemovableInspectorDateLabel(row, activity, { compact: true }))
      ) + cellHorizontalPaddingPx
    );
  }, [activity, isEmpty, selectedInspectors]);

  const getRowProjectRoleId = (row: {
    id: number;
    currentProjectRoleId?: number;
  }): number | undefined => {
    const fromMap = inspectorProjectRoles?.get(row.id);
    return fromMap != null ? fromMap : row.currentProjectRoleId;
  };

  const getInspectorDurationLabel = (
    row: InspectorDTO & {
      selectedDurationDays?: number;
      countsTowardAudit?: boolean;
      currentProjectRoleId?: number;
    }
  ): string => {
    const roleId = getRowProjectRoleId(row);
    const role = roleId != null ? projectRoles.find((r) => r.id === roleId) : undefined;
    const isNonCounting =
      row.countsTowardAudit === false ||
      (row.countsTowardAudit == null && role?.countsTowardAudit === false);

    if (
      row.selectedDurationDays != null &&
      Number.isFinite(Number(row.selectedDurationDays)) &&
      Number(row.selectedDurationDays) > 0
    ) {
      return String(roundDuration(Number(row.selectedDurationDays)));
    }

    const inspectorAppointments =
      activity?.appointments?.filter(
        (apt) =>
          apt.inspector?.id === row.id &&
          apt.type !== 'blocker' &&
          (isNonCounting
            ? apt.projectRole?.countsTowardAudit === false
            : apt.projectRole?.countsTowardAudit !== false)
      ) ?? [];

    const days = roundDuration(
      inspectorAppointments.reduce((sum, apt) => sum + appointmentDisplayDurationDays(apt), 0)
    );

    return days > 0 ? String(days) : '—';
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: t('allocation.modal.grid.columns.name'),
      renderCell: ({ row }: any) => {
        if (row.isPlaceholder) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: spacingUtil(2) }}>
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: 'transparent',
                  color: 'text.disabled',
                }}
              >
                <Iconify icon="eva:person-add-outline" width={24} />
              </Avatar>
              <Typography variant="body2">—</Typography>
            </Box>
          );
        }

        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: spacingUtil(2),
              maxWidth: '100%',
              overflow: 'hidden',
            }}
          >
            <UserIcon id={row.id} name={row.name} type={row.type} />
            <Box
              sx={{ display: 'flex', flexDirection: 'column', maxWidth: '100%', overflow: 'hidden' }}
            >
              <Typography variant="body2">{row.name}</Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '100%',
                }}
              >
                {formatInspectorCityStateCountry(row)}
              </Typography>
            </Box>
          </Box>
        );
      },
      minWidth: 140,
      flex: 2.5,
    },
    {
      field: 'projectRole',
      headerName: t('allocation.modal.grid.columns.projectRole'),
      renderCell: ({ row }: any) => {
        if (row.isPlaceholder) {
          return (
            <Typography variant="body2" color="text.primary">
              —
            </Typography>
          );
        }

        const roleId = getRowProjectRoleId(row);
        const role = roleId != null ? projectRoles.find((r) => r.id === roleId) : undefined;
        const isDrafted = activity.status === ActivityStatus.DRAFTED;
        // Pending selection can always pick a role. Drafted allocated inspectors
        // can persist a role change only when no pending team exists.
        const canEditRole =
          Boolean(onProjectRoleChange) &&
          (removableIds.has(row.id) || (isDrafted && removableIds.size === 0));
        const isLoading = projectRolesLoading;
        let filteredRoles = isLoading ? [] : [...projectRoles];
        if (isDrafted) {
          filteredRoles = filteredRoles.filter((projectRole) =>
            Boolean(projectRole.countsTowardAudit)
          );
          // Keep the current assignment visible even if it is non-counting,
          // so the select value matches an option.
          if (role && !filteredRoles.some((projectRole) => projectRole.id === role.id)) {
            filteredRoles =
              Boolean(role.countsTowardAudit) || removableIds.has(row.id)
                ? [role, ...filteredRoles]
                : [role];
          }
        }
        const consumedUniqueRoleIds = getConsumedUniqueProjectRoleIds(
          row.id,
          inspectorProjectRoles ?? new Map(),
          projectRoles
        );
        const isProjectRoleLocked = !isProjectRoleEditable(activity.status as ActivityStatus);

        if (!canEditRole) {
          if (!role) {
            return (
              <Typography variant="body2" color="text.secondary">
                —
              </Typography>
            );
          }

          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
              <Typography variant="body2" noWrap>
                {role.name}
              </Typography>
              {!role.countsTowardAudit && (
                <Typography variant="caption" color="text.secondary" noWrap>
                  ({t('allocation.modal.grid.columns.nonCounting')})
                </Typography>
              )}
            </Box>
          );
        }

        return (
          <FormControl size="small" fullWidth>
            <Select
              value={roleId ?? ''}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '') {
                  return;
                }
                onProjectRoleChange?.(row.id, Number(value));
              }}
              displayEmpty
              disabled={
                !canEditRole ||
                isSaving ||
                isProjectRoleLocked ||
                isLoading ||
                filteredRoles.length === 0
              }
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="" disabled>
                <em>
                  {isLoading
                    ? t('allocation.modal.grid.columns.loadingRoles')
                    : filteredRoles.length === 0
                      ? t('allocation.modal.grid.columns.noRolesAvailable')
                      : t('allocation.modal.grid.columns.selectRole')}
                </em>
              </MenuItem>
              {filteredRoles.map((role) => {
                const isUniqueTaken = consumedUniqueRoleIds.has(role.id);
                return (
                  <MenuItem key={role.id} value={role.id} disabled={isUniqueTaken}>
                    {role.name}
                    {!role.countsTowardAudit && (
                      <Typography
                        component="span"
                        variant="caption"
                        color="text.secondary"
                        sx={{ ml: 1 }}
                      >
                        ({t('allocation.modal.grid.columns.nonCounting')})
                      </Typography>
                    )}
                    {isUniqueTaken && (
                      <Typography
                        component="span"
                        variant="caption"
                        color="text.secondary"
                        sx={{ ml: 1 }}
                      >
                        ({t('allocation.modal.grid.columns.uniqueRoleTaken')})
                      </Typography>
                    )}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        );
      },
      minWidth: 120,
      flex: 1.5,
    },
    {
      field: 'selectedDurationDays',
      headerName: t('allocation.details.filters.durationDays.label'),
      renderCell: ({ row }: any) => {
        const label = row.isPlaceholder ? '—' : getInspectorDurationLabel(row);
        const days = Number(row.selectedDurationDays);
        const isLocked = !isProjectRoleEditable(activity.status as ActivityStatus);
        // Pending counting rows are editable even at zero days. Redistribution hands a new
        // auditor 0 when a manual row already claims the whole duration, and that row has
        // to stay typeable — otherwise Save is blocked by a value nobody can reach.
        // Already-allocated rows carry no `selectedDurationDays`, so they stay read-only.
        const isEditable =
          !row.isPlaceholder &&
          !isLocked &&
          !isSaving &&
          Boolean(onInspectorDurationChange) &&
          isDurationRowEditable(row);

        if (!isEditable) {
          return (
            <Typography variant="body2" sx={{ width: '100%', textAlign: 'center' }}>
              {label}
            </Typography>
          );
        }

        // What this row may hold: the activity duration less the days the other counting
        // rows already claim.
        const otherCountingTotal = selectedInspectors
          .filter(
            (other) =>
              other.id !== row.id &&
              other.countsTowardAudit !== false &&
              other.selectedDurationDays != null &&
              Number.isFinite(Number(other.selectedDurationDays))
          )
          .reduce((sum, other) => sum + Number(other.selectedDurationDays), 0);
        const isNonCountingRow = row.countsTowardAudit === false;
        const maxForRow = isNonCountingRow
          ? MAX_NON_COUNTING_ALLOCATION_DAYS
          : roundDuration(Math.max(0, getActivityDurationDays(activity) - otherCountingTotal));

        return (
          <InspectorDurationInput
            value={days}
            maxDays={maxForRow}
            allowClear={isNonCountingRow}
            onCommit={(value) => onInspectorDurationChange?.(row.id, value)}
          />
        );
      },
      minWidth: 110,
      width: 130,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'competencies',
      headerName: t('allocation.modal.grid.columns.competencies'),
      renderCell: ({ row }: any) =>
        row.isPlaceholder ? (
          <Typography variant="body2">—</Typography>
        ) : (
          <AuthorizationsTooltip
            containerIdentifier={`selected-inspector-auth-${row.id}`}
            accreditationBodyIds={accreditationBodyIds}
            authorizations={row.authorizations}
            activityCompetencyIds={
              activityCompetencyIds ??
              activity?.activityCompetencies?.map((c) => c.competency?.id)?.filter(Boolean) ?? []
            }
            competencyHierarchy={competencyHierarchy}
          />
        ),
      minWidth: 140,
      flex: 2,
    },
    {
      field: 'travelDistance',
      headerName: t('allocation.modal.grid.columns.distance'),
      renderCell: ({ row }: any) => {
        if (row.isPlaceholder) {
          return <Typography variant="body2">—</Typography>;
        }

        const isRowDistanceLoading =
          distanceLoading &&
          row.travelDistance == null &&
          row.straightLineDistance == null &&
          activity?.type !== ActivityType.DeskService;

        return (
          <InspectorDistanceCell
            row={row}
            siteLocation={activity?.site?.location}
            isDeskService={activity?.type === ActivityType.DeskService}
            distanceLoading={isRowDistanceLoading}
            compactLoading
            showDirections={false}
          />
        );
      },
      minWidth: 72,
      width: 80,
      flex: 0,
    },
    {
      field: 'businessUnit',
      headerName: t('allocation.modal.grid.columns.businessUnit'),
      renderCell: ({ row }: any) =>
        row.isPlaceholder ? '—' : <BusinessUnitCell inspector={row} />,
      minWidth: 72,
      flex: 0.8,
    },
    {
      field: 'scheduled',
      headerName: '',
      minWidth: scheduledColumnWidth,
      width: scheduledColumnWidth,
      flex: 0,
      renderCell: ({ row }: any) => {
        if (row.isPlaceholder) {
          return null;
        }

        const dateLabel = getRemovableInspectorDateLabel(row, activity, { compact: true });
        const dateTitle = getRemovableInspectorDateLabel(row, activity, { compact: false });
        const title = dateTitle !== dateLabel ? dateTitle : undefined;

        if (removableIds.has(row.id)) {
          return (
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              disabled={isSaving}
              title={title}
              onClick={() => onRemoveInspector?.(row.id)}
              endIcon={<Iconify icon="eva:close-fill" width={16} />}
              sx={allocationDatePillSx}
            >
              {dateLabel}
            </Button>
          );
        }

        return (
          <Typography variant="body2" title={title} noWrap>
            {dateLabel}
          </Typography>
        );
      },
    },
  ];

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
        bgcolor: 'background.paper',
        mt: spacingUtil(1),
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{ p: 2 }}
      >
        <Typography variant="h6">
          {t('activityModal.details.sections.team.title')}
        </Typography>
        {!isEmpty && (
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              size="small"
              color="inherit"
              disabled={!canRevert}
              onClick={onRevert}
              startIcon={<Iconify icon="eva:undo-fill" width={18} />}
            >
              {t('actions.revert')}
            </Button>
            <Button
              variant="contained"
              size="small"
              color="inherit"
              disabled={!canSave}
              onClick={onSave}
              endIcon={<Iconify icon="eva:checkmark-fill" width={18} />}
              sx={{
                bgcolor: 'text.primary',
                color: 'common.white',
                '&:hover': { bgcolor: 'text.primary' },
              }}
            >
              {t('actionPanel.approve')}
            </Button>
          </Stack>
        )}
      </Stack>
      <Box
        sx={{
          maxHeight: 280,
          overflow: 'auto',
          px: 1,
          pb: 1,
        }}
      >
        <AppDataGrid
          rows={rows}
          columns={columns}
          getRowHeight={() => 'auto'}
          columnHeaderHeight={32}
          autoHeight
          hideFooter
          disableRowSelectionOnClick
          sx={{
            border: 0,
            borderRadius: 0,
            '.MuiDataGrid-columnHeaders': {
              position: 'sticky',
              top: 0,
              zIndex: 1,
              bgcolor: 'background.paper',
            },
            '.MuiDataGrid-columnHeader:focus-within, .MuiDataGrid-cell:focus-within': {
              outline: 'none',
            },
            [`& .${gridClasses.cell}`]: {
              alignItems: 'center',
              display: 'inline-flex',
              padding: spacingUtil(1),
              overflow: 'visible',
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default SelectedInspectorsList;
